import { defineStore } from 'pinia'

// A failed load leaves every feature gate off for the whole SPA session, so
// sessions that survive the initial page load get one more attempt (VIP-12234).
const RETRY_DELAY = 10000
// Tracking waits for the features via whenReady, but must never hang on them.
const READY_TIMEOUT = 3000

export const useFeaturesStore = defineStore('features', {
  state: () => ({
    features: []
  }),
  getters: {
    isFeatureEnabled:
      ({ features }) =>
      (name) =>
        features.some(({ feature_name: n, is_active: a }) => n === name && a),
    gatekeeperForElbwalker: ({ features }) => ({
      gatekeeper_settings: features.reduce(
        (col, { feature_name: name, is_active: active }) => ({
          ...col,
          [name]: active
        }),
        {}
      )
    })
  },
  actions: {
    // bustCache drops the service worker's 'gatekeeper' cache before
    // fetching: workbox serves this endpoint CacheFirst for an hour (see
    // vite.config.js), so after a login/logout the cached response still
    // belongs to the previous identity.
    async refresh({ bustCache = false } = {}) {
      const internals = _internalsFor(this)
      // Joins an already running load instead of firing a duplicate request
      // (e.g. the boot refresh and the customer-id watcher in App.vue).
      clearTimeout(internals.retryTimer)
      if (bustCache || !internals.pending) _startLoad(this, { bustCache })
      await internals.pending
    },
    // Resolves once the current load attempt has settled or after a short
    // timeout, whichever comes first. Never rejects, so callers (tracking)
    // cannot hang on a blocked or slow request. Callers that hold the page
    // back while they wait pass a shorter timeout.
    async whenReady(timeout = READY_TIMEOUT) {
      const { pending } = _internalsFor(this)
      if (!pending) return
      await Promise.race([
        pending,
        new Promise((resolve) => setTimeout(resolve, timeout))
      ])
    }
  }
})

// In-flight load and retry timer, kept per store instance rather than in state:
// they are internals, and a Promise has no business being reactive or visible in
// devtools. Module-scope variables would be shared by every Pinia instance,
// which leaks between test cases.
const _internals = new WeakMap()

const _internalsFor = (store) => {
  if (!_internals.has(store)) _internals.set(store, { pending: null })
  return _internals.get(store)
}

const _startLoad = (store, opts) => {
  const internals = _internalsFor(store)
  const load = _load(store, opts).finally(() => {
    // Guard against an old, superseded load clearing the new one.
    if (internals.pending === load) internals.pending = null
  })
  internals.pending = load
  return load
}

const _load = async (store, { bustCache = false, isRetry = false }) => {
  try {
    // Inside the try on purpose: this dynamic import can fail on its own (stale
    // client requesting an old chunk hash after a deploy) and the load must
    // still not reject, or whenReady's callers drop their tracking event
    // entirely instead of merely missing the gatekeeper settings.
    const { ApiController } = await _getModules()
    if (bustCache) await globalThis.caches?.delete('gatekeeper')
    const response = await ApiController.fetchCustomerFeatures()
    store.features = response ?? []
    await _trackGatekeeper(store.features)
  } catch {
    // Mostly aborted page loads and privacy blockers; retrying once covers
    // the sessions that stay alive with a temporary network problem.
    if (!isRetry) {
      _internalsFor(store).retryTimer = setTimeout(
        () => _startLoad(store, { bustCache, isRetry: true }),
        RETRY_DELAY
      )
    }
  }
}

const _trackGatekeeper = async (data) => {
  const {
    TagManager,
    GA4_CUSTOM_EVENT: { USER_DATA }
  } = await _getModules()
  const settings = data.reduce(
    (col, { feature_name: name, is_active: active }) => ({
      ...col,
      [name]: active
    }),
    {}
  )
  TagManager.pushEvent(USER_DATA, { gatekeeper_settings: settings })
  // Awaited on purpose: whenReady must guarantee that the walker user data
  // is up to date, so this cannot be deferred with a timeout.
  if (typeof globalThis.window?.elb === 'function') {
    const { elb } = await import('@elbwalker/walker.js')
    elb('walker user', { gatekeeper_settings: settings })
  }
}

const _getModules = (() => {
  let cache

  return async () => {
    if (cache) return cache

    const [
      { default: TagManager },
      { default: ApiController },
      { GA4_CUSTOM_EVENT }
    ] = await Promise.all([
      import('@/services/analytics/TagManager'),
      import('@/services/ApiController'),
      import('@/constants/GlobalConstants')
    ])
    cache = { ApiController, TagManager, GA4_CUSTOM_EVENT }
    return cache
  }
})()
